import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  createResourceObjectMonetaryValue,
  updateResourceObjectMonetaryValue,
} from "@/orval/gen"
import { ResourceObjectMonetaryValue } from "../types"

interface UseValuationLogicProps {
  resourceObjectId: number
  monetaryValues: ResourceObjectMonetaryValue[]
}

interface CreateValuationFormData {
  monetaryValue: number | undefined
  estimateDate: Date | null
  valuation: string
  estimatedByPersonId: number | null
  existingValuationId?: number
}

export function useValuationLogic({
  resourceObjectId,
  monetaryValues,
}: UseValuationLogicProps) {
  const queryClient = useQueryClient()

  const [isCreateValuationModalOpen, setIsCreateValuationModalOpen] =
    React.useState(false)
  const [createValuationFormData, setCreateValuationFormData] =
    React.useState<CreateValuationFormData>({
      monetaryValue: undefined,
      estimateDate: new Date(),
      valuation: "",
      estimatedByPersonId: null,
    })

  const bestEstimate = React.useMemo(() => {
    if (!monetaryValues.length) return null

    const estimates = monetaryValues
      .filter((m) => m.estimateDate)
      .map((m) => ({
        ...m,
        date:
          m.estimateDate instanceof Date
            ? m.estimateDate
            : new Date(m.estimateDate || 0),
      }))
      .sort((a, b) => b.date.getTime() - a.date.getTime())

    return estimates.length > 0 ? estimates[0] : null
  }, [monetaryValues])

  const createValuationMutation = useMutation({
    mutationFn: async (data: CreateValuationFormData) => {
      const basePayload = {
        resourceObjectId,
        monetaryValue: data.monetaryValue ?? 0,
        estimateDate: data.estimateDate
          ? new Date(data.estimateDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        valuation: data.valuation || null,
        estimatedByPersonId: data.estimatedByPersonId,
      }

      const isUpdate = !!data.existingValuationId

      if (isUpdate) {
        const updatePayload = {
          resourceObjectMonetaryValueId: data.existingValuationId!,
          ...basePayload,
        }
        return updateResourceObjectMonetaryValue(updatePayload)
      } else {
        return createResourceObjectMonetaryValue(basePayload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectMonetaryValues", resourceObjectId],
      })
      setIsCreateValuationModalOpen(false)
      setCreateValuationFormData({
        monetaryValue: undefined,
        estimateDate: new Date(),
        valuation: "",
        estimatedByPersonId: null,
      })
    },
  })

  return {
    isCreateValuationModalOpen,
    setIsCreateValuationModalOpen,
    createValuationFormData,
    setCreateValuationFormData,
    createValuationMutation,
    bestEstimate,
  }
}
